import { Align, Arrange, Icon, Link, Spacer, SvgAdd, Text } from '@loomhq/lens';
import React, { useEffect } from 'react';

import loomIcon from 'img/logos/loom-connect-logo.svg';
import slackIcon from 'img/logos/slack-connect-logo.svg';

import * as analytics from '../../../../utilities/analytics';
import { EMBED_LOGGED_OUT_SCREEN_VIEWED } from '../../../../constants/events';
import { LOGIN_PAGE, LOOM_URI } from '../../../../constants/routes';
import { useFlagIsActivated } from '../../../../hooks/featureFlag';
import { invokeSharedAuthFlow } from '../../../../utilities/slack/sharedAuthHandler';
import { APP_SOURCE_SLACK_DESKTOP } from '../../../../../../shared/constants/analytics';
import { ROLLOUT_SLACK_SHARED_AUTH_PRIVATE } from '../../../../../../shared/constants/featureFlag';
import { getloginUrlWithRedirect } from '../../../../../../shared/utilities/url';

import Button from 'components/ui-components/button';

import styles from './styles.module.less';

const SHARED_AUTH_ENTRY_POINT_PRIVATE_VIDEO = 'private logged out screen';

/*
 * This component is responsible for 3 code paths:
 * 1. The viewer is trying to view a video that has email gating and they are not
 *    in this list.
 * 2. The viewer is trying to view a video that has email gating and they are logged
 *    out.
 * 3. The viewer is trying to view a video that has email gating, they are in this
 *    list, but their email is not verified.
 *
 * We always surface this UI from path #3 if the user does not have their email
 * verified in an attempt to get them to verify even if they are not in the list
 * of permitted viewers.
 *
 * It is used in 2 places:
 * 1. At the top-level of the share video page to gate all page interaction.
 * 2. Within the embed video player in case the video is embedded on an external
 *    site
 */

const LoggedOutScreen = ({
  redirect,
  isMainVideoOnSharePage,
  isFromSlackDesktopApp,
  videoId,
}) => {
  const isSlackSharedAuthV2 = useFlagIsActivated({
    flag: ROLLOUT_SLACK_SHARED_AUTH_PRIVATE,
    activationValues: [true],
  });

  useEffect(() => {
    if (isSlackSharedAuthV2 !== undefined) {
      const props = {
        slack_shared_auth_enabled: true,
        slack_shared_auth_v2: isSlackSharedAuthV2,
      };

      if (isFromSlackDesktopApp) {
        props.source = APP_SOURCE_SLACK_DESKTOP;
      }

      analytics.track(EMBED_LOGGED_OUT_SCREEN_VIEWED, props);
    }
  }, [isSlackSharedAuthV2, isFromSlackDesktopApp]);

  if (isFromSlackDesktopApp) {
    return (
      <SlackSharedAuthScreen
        videoId={videoId}
        isSlackSharedAuthV2={isSlackSharedAuthV2}
      />
    );
  }

  const modalBody = isMainVideoOnSharePage
    ? 'Public viewing has been disabled by the video owner. Login to view or request access.'
    : 'Due to the privacy settings for this video, it cannot be played here at this time.';

  let modalAction;

  if (isMainVideoOnSharePage) {
    modalAction = (
      <>
        <Button
          onClicked={() => {
            window.location = getloginUrlWithRedirect(
              redirect,
              'Please Login to view this video',
              LOOM_URI + LOGIN_PAGE
            );
          }}
          capitalize={false}
          buttonType="rp-primary-2"
          value="Login"
        />
        <Spacer top="medium">
          <Link href={`${LOGIN_PAGE}?signup=1`}>Create an Account</Link>
        </Spacer>
      </>
    );
  } else {
    modalAction = (
      <a
        rel="noopener noreferrer"
        target="_blank"
        href={`${LOOM_URI}/share/${videoId}`}
      >
        <Button
          capitalize={false}
          buttonType="rp-primary-3"
          value="View it on Loom"
        />
      </a>
    );
  }

  return (
    <>
      <Text
        className="mb:small"
        alignment="center"
        fontWeight="bold"
        size="xlarge"
      >
        Sorry
      </Text>
      <div className="loom-permissions-modal-body">{modalBody}</div>
      {modalAction}
    </>
  );
};

const SlackSharedAuthScreen = ({ videoId, isSlackSharedAuthV2 }) => {
  if (isSlackSharedAuthV2) {
    return (
      <>
        <div className={styles.slackSharedAuthIcons}>
          <Align alignment="center">
            <Arrange gap="0px">
              <img src={slackIcon} />
              <Icon color="blurpleMedium" icon={<SvgAdd />} size={2} />
              <img src={loomIcon} />
            </Arrange>
          </Align>
        </div>
        <Spacer bottom="large">
          <Text alignment="center">
            Connect your Loom account to watch videos, leave comments, or give
            reactions from Slack.
          </Text>
        </Spacer>
        <Button
          capitalize={false}
          buttonType="rp-primary-3"
          value="Connect my account"
          onClicked={() =>
            invokeSharedAuthFlow(
              videoId,
              SHARED_AUTH_ENTRY_POINT_PRIVATE_VIDEO,
              isSlackSharedAuthV2
            )
          }
        />
      </>
    );
  }

  return (
    <>
      <Text
        className="mb:small"
        alignment="center"
        fontWeight="bold"
        size="xlarge"
      >
        Sorry
      </Text>
      <Spacer bottom="large">
        <Text alignment="center">
          Due to the privacy settings for this video, it cannot be played here
          at this time.
        </Text>
      </Spacer>
      <Button
        capitalize={false}
        buttonType="rp-primary-3"
        value="View it on Loom"
        onClicked={() =>
          invokeSharedAuthFlow(
            videoId,
            SHARED_AUTH_ENTRY_POINT_PRIVATE_VIDEO,
            isSlackSharedAuthV2
          )
        }
      />
    </>
  );
};

export default LoggedOutScreen;
